import { useEffect, useState } from 'react'
import { Paper, Title, Text, Group, Stack, Loader, Center, Alert, RingProgress, Progress } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { getAuthMethodStats, getErrorMessage } from '../../api/loginActivityApi'
import type { AuthMethodStatsResponse } from '../../api/loginActivityApi'

const METHOD_LABELS: Record<string, string> = {
  password: 'Password',
  passkey: 'Passkey',
  magic_link: 'Magic link',
  handoff: 'Handoff',
}

const METHOD_COLORS: Record<string, string> = {
  password: 'gray',
  passkey: 'blue',
  magic_link: 'grape',
  handoff: 'cyan',
}

// Fixed display order so the bar and legend read the same way every render, regardless of
// which methods happened to appear in the aggregation this window.
const METHOD_ORDER = ['password', 'passkey', 'magic_link', 'handoff']

export function PasskeyAdoptionCard() {
  const [stats, setStats] = useState<AuthMethodStatsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    getAuthMethodStats()
      .then((res) => {
        setStats(res)
        setLoading(false)
      })
      .catch((err) => {
        setError(getErrorMessage(err))
        setLoading(false)
      })
  }, [])

  const { usersWithPasskey, activeUsers } = stats?.passkeyAdoption ?? { usersWithPasskey: 0, activeUsers: 0 }
  const adoptionPct = activeUsers > 0 ? Math.round((usersWithPasskey / activeUsers) * 100) : 0

  const totalLogins = stats ? Object.values(stats.counts).reduce((sum, n) => sum + n, 0) : 0
  const methodBreakdown = METHOD_ORDER.filter((m) => (stats?.counts[m] ?? 0) > 0).map((m) => ({
    method: m,
    count: stats!.counts[m],
    pct: totalLogins > 0 ? (stats!.counts[m] / totalLogins) * 100 : 0,
  }))

  return (
    <Paper withBorder radius="md" p="lg">
      <Group justify="space-between" mb="md">
        <Title order={4}>Passkey Adoption</Title>
        {!loading && !error && stats && (
          <Text size="sm" c="dimmed">
            Logins by method, last {stats.windowDays} days
          </Text>
        )}
      </Group>

      {loading && (
        <Center py="md">
          <Loader size="sm" />
        </Center>
      )}

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          {error}
        </Alert>
      )}

      {!loading && !error && stats && (
        <Group align="center" gap="xl" wrap="wrap">
          <Group gap="md">
            <RingProgress
              size={90}
              thickness={9}
              roundCaps
              sections={[{ value: adoptionPct, color: 'blue' }]}
              label={
                <Text size="sm" fw={600} ta="center">
                  {adoptionPct}%
                </Text>
              }
            />
            <div>
              <Text fw={500}>{usersWithPasskey} user{usersWithPasskey !== 1 ? 's' : ''}</Text>
              <Text size="sm" c="dimmed">
                of {activeUsers} active have a passkey
              </Text>
            </div>
          </Group>

          <Stack gap={6} style={{ flex: 1, minWidth: 220 }}>
            {totalLogins === 0 ? (
              <Text size="sm" c="dimmed">
                No logins recorded in this window
              </Text>
            ) : (
              <>
                <Progress.Root size={20}>
                  {methodBreakdown.map(({ method, pct }) => (
                    <Progress.Section key={method} value={pct} color={METHOD_COLORS[method] ?? 'gray'} />
                  ))}
                </Progress.Root>
                <Group gap="md">
                  {methodBreakdown.map(({ method, count }) => (
                    <Group key={method} gap={6}>
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 2,
                          backgroundColor: `var(--mantine-color-${METHOD_COLORS[method] ?? 'gray'}-6)`,
                        }}
                      />
                      <Text size="xs" c="dimmed">
                        {METHOD_LABELS[method] ?? method}: {count}
                      </Text>
                    </Group>
                  ))}
                </Group>
              </>
            )}
          </Stack>
        </Group>
      )}
    </Paper>
  )
}
